import { SiteHeader } from "../../components/site/SiteHeader";
import { SiteFooter } from "../../components/site/SiteFooter";
import { PageHero } from "../../components/site/PageHero";
import { Chapter } from "../../components/site/Chapter";
import { CTABand } from "../../components/site/CTABand";
import { Reveal, Stagger, StaggerItem } from "../../components/motion/Reveal";
import { DISTRICTS } from "../../data/content";

const PRINCIPLES = [
  { title: "Solutions before products", text: "We design what your site actually needs. Equipment follows the design — never the other way around." },
  { title: "One accountable partner", text: "Consulting, supply, installation and support under one roof. No gaps between vendors, no passed blame." },
  { title: "Documented, always", text: "Every design, configuration and handover is documented — so your infrastructure stays manageable for years." },
  { title: "Honest by default", text: "No inflated claims, no fabricated credentials. We let real work, real references and real support speak." },
];

const ABOUT_IMG = "https://images.unsplash.com/photo-1567157577867-05ccb1388e66?auto=format&fit=crop&w=1100&q=70";

export default function About() {
  return (
    <div className="font-publicsans bg-nx-navy text-slate-200 antialiased" data-testid="about-page">
      <SiteHeader />
      <PageHero
        crumb="About"
        eyebrow="About NexSecure"
        title="A technology partner"
        titleAccent="rooted in Kerala."
        description="NexSecure Technologies is a Technology Infrastructure & Integrated Solutions company based in Ernakulam — engineering security, networking, communication, automation and IT into one dependable whole."
      />

      <section className="py-16 sm:py-24 border-b border-white/10">
        <div className="nx-wrap grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <Chapter no="01" eyebrow="Who We Are" title="Engineering-driven. Integration-first." text="We exist because buildings and organisations need their technology to work together — not as isolated purchases, but as one intelligent infrastructure. That is what we design, deliver and support." />
            <Reveal delay={0.25}>
              <div className="mt-8 flex flex-wrap gap-2" data-testid="about-districts">
                {DISTRICTS.slice(0, 8).map((d) => (
                  <span key={d} className="text-[11px] tracking-wider uppercase text-slate-400 border border-white/15 px-3 py-1.5">{d}</span>
                ))}
                <span className="text-[11px] tracking-wider uppercase text-nx-goldlight border border-nx-gold/40 px-3 py-1.5">+ all 14 districts</span>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="absolute -inset-3 border border-nx-gold/25 pointer-events-none" />
              <div className="nx-clip overflow-hidden">
                <img src={ABOUT_IMG} alt="Indian coastal business district at dusk" className="w-full h-72 sm:h-96 object-cover" loading="lazy" data-testid="about-image" />
              </div>
              <p className="mt-4 text-[10px] tracking-[0.24em] uppercase text-slate-500">Working cities, working infrastructure</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-nx-navy2 border-b border-white/10">
        <div className="nx-wrap">
          <Chapter no="02" eyebrow="How We Think" title="Principles we won't compromise." className="mb-14" />
          <Stagger className="grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {PRINCIPLES.map((p, i) => (
              <StaggerItem key={p.title}>
                <div className="group h-full bg-nx-navy2 hover:bg-nx-navy p-8 transition-colors" data-testid={`about-principle-${i}`}>
                  <span className="font-marcellus text-nx-gold/60 text-lg">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-marcellus text-xl text-white mt-4 mb-3">{p.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{p.text}</p>
                  <span className="block mt-6 h-px w-8 bg-nx-gold/50 group-hover:w-full transition-all duration-500" />
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="nx-wrap grid lg:grid-cols-2 gap-14">
          <div>
            <Chapter no="03" eyebrow="Vision" title="Intelligent infrastructure, everywhere it matters." text="A Kerala — and eventually an India — where every organisation's technology works as one quiet, dependable system: secure, connected and future-ready." />
          </div>
          <div>
            <Chapter no="04" eyebrow="Mission" title="Design honestly. Deliver completely. Support for the long term." text="To be the most trusted technology infrastructure partner in every district we serve — measured not by what we sell, but by how reliably our clients' systems run, year after year." />
          </div>
        </div>
      </section>

      <CTABand />
      <SiteFooter />
    </div>
  );
}
